import { useEffect, useRef, useState } from "react";
import "./ScrollbarArrows.scss";

interface ScrollbarArrowsProps {
  targetId: string;
  tablesScroll?: number;
}

const ScrollbarArrows = ({
  targetId,
  tablesScroll = 0,
}: ScrollbarArrowsProps) => {
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [parentClasses, setParentClasses] =
    useState<string>("scrollbar-arrows");
  const targetRef = useRef<HTMLElement | null>(null);
  const scrollAnimationRef = useRef<number | null>(null);
  const updateArrowVisibilityRef = useRef<(() => void) | null>(null);

  // Check if RTL
  const isRtl = () => {
    const body = document.body;
    return body.classList.contains("app-content-rtl");
  };

  // Handle scroll animation
  const scroll = (direction: "left" | "right") => {
    // Try to get the target element again in case ref wasn't set
    const target = targetRef.current || document.getElementById(targetId);
    if (!target) {
      console.warn(
        `ScrollbarArrows: Target element with id "${targetId}" not found`
      );
      return;
    }

    const rtl = isRtl();
    const scrollAmount = 300;
    const currentScroll = target.scrollLeft;
    let newScrollLeft: number;

    if (direction === "left") {
      newScrollLeft = rtl
        ? currentScroll + scrollAmount
        : currentScroll - scrollAmount;
    } else {
      newScrollLeft = rtl
        ? currentScroll - scrollAmount
        : currentScroll + scrollAmount;
    }

    // Cancel any ongoing animation
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
    }

    // Smooth scroll animation
    const startScroll = currentScroll;
    const distance = newScrollLeft - startScroll;
    const duration = 500; // 500ms
    let startTime: number | null = null;

    const animateScroll = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const newScroll = startScroll + distance * easeOut;
      target.scrollLeft = newScroll;

      if (progress < 1) {
        scrollAnimationRef.current = requestAnimationFrame(animateScroll);
      } else {
        scrollAnimationRef.current = null;
        // Update visibility after animation completes
        if (updateArrowVisibilityRef.current) {
          updateArrowVisibilityRef.current();
        }
      }
    };

    scrollAnimationRef.current = requestAnimationFrame(animateScroll);
  };

  useEffect(() => {
    // Update arrow visibility based on scroll position and slides count
    const updateArrowVisibility = () => {
      const target = targetRef.current || document.getElementById(targetId);
      if (!target) return;

      const actualTablesScroll = tablesScroll;
      const windowWidth = window.innerWidth;

      // Check scroll position
      const scrollValue = Math.abs(target.scrollLeft);
      const scrollWidth = target.scrollWidth;
      const width = target.offsetWidth;
      const threshold = 1;

      const isAtEnd = scrollWidth - scrollValue - width <= threshold;
      const isAtStart = scrollValue === 0;

      // Determine visibility based on slides count and window width
      let showLeft = false;
      let showRight = false;
      let classes = "scrollbar-arrows";

      if (actualTablesScroll <= 3 && windowWidth >= 1200) {
        // Hide both arrows
        showLeft = false;
        showRight = false;
      } else if (actualTablesScroll <= 3 && windowWidth < 1200) {
        // Hide left arrow, show right arrow conditionally
        showLeft = false;
        showRight = !isAtEnd;
      } else if (actualTablesScroll > 3) {
        // Show arrows based on scroll position
        showLeft = !isAtStart;
        showRight = !isAtEnd;
      } else {
        // Default: hide both
        showLeft = false;
        showRight = false;
      }

      // Update classes based on scroll position
      if (isAtEnd) {
        classes += " end-right";
        showRight = false;
      } else if (isAtStart) {
        classes += " end-left";
        showLeft = false;
        if (actualTablesScroll <= 3 && windowWidth < 1200) {
          // Keep right arrow visible for small screens with <= 3 slides
          showRight = true;
        }
      } else {
        classes += " scrolling";
      }

      setShowLeftArrow(showLeft);
      setShowRightArrow(showRight);
      setParentClasses(classes);
    };

    // Store reference to updateArrowVisibility for use in scroll function
    updateArrowVisibilityRef.current = updateArrowVisibility;

    // Find target element - use a small delay to ensure DOM is ready
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cleanupFn: (() => void) | null = null;

    const findTarget = () => {
      const target = document.getElementById(targetId);
      if (target) {
        targetRef.current = target;
        // Initial update
        updateArrowVisibility();

        // Add event listeners
        const handleResize = () => {
          updateArrowVisibility();
        };

        const handleScroll = () => {
          updateArrowVisibility();
        };

        window.addEventListener("resize", handleResize);
        target.addEventListener("scroll", handleScroll);

        // Cleanup function
        cleanupFn = () => {
          window.removeEventListener("resize", handleResize);
          target.removeEventListener("scroll", handleScroll);
          if (scrollAnimationRef.current) {
            cancelAnimationFrame(scrollAnimationRef.current);
          }
        };
      } else {
        // Retry after a short delay if element not found
        timeoutId = setTimeout(findTarget, 100);
      }
    };

    findTarget();

    // Return cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (cleanupFn) {
        cleanupFn();
      }
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
    };
  }, [targetId, tablesScroll]);

  return (
    <div className={parentClasses}>
      <button
        className="scrollbar-arrow scrollbar-arrow--left"
        data-target={targetId}
        style={{ display: showLeftArrow ? "block" : "none" }}
        onClick={() => scroll("left")}
      >
        <i className="f-icon chevron-left-icon"></i>
      </button>
      <button
        className="scrollbar-arrow scrollbar-arrow--right"
        data-target={targetId}
        style={{ display: showRightArrow ? "block" : "none" }}
        onClick={() => scroll("right")}
      >
        <i className="f-icon chevron-right-icon"></i>
      </button>
    </div>
  );
};

export default ScrollbarArrows;
